import { apiErrorSchema, loginOkResponseSchema, type ApiError } from '@gb-ticket/shared-types';
import { type z } from 'zod';

/** Base URL of the backend API. In dev, Vite proxies `/api` to NestJS. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Public auth routes must not trigger a silent refresh (avoids loops). */
const AUTH_NO_REFRESH = new Set([
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/2fa/enable',
  '/auth/password/forgot',
  '/auth/password/reset',
  '/auth/sso-status',
]);

export class ApiRequestError extends Error {
  readonly error: ApiError;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.error = error;
  }
}

type FetchOptions = {
  skipRefresh?: boolean;
};

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Minimal typed fetch wrapper. Responses are validated with the given Zod schema so
 * that contract drift between backend and frontend surfaces immediately.
 *
 * On 401, a single silent `POST /auth/refresh` is attempted (deduped across concurrent
 * calls, and across tabs via Web Locks when available) before the original request is retried.
 */
export async function apiFetch<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  init?: RequestInit,
  options?: FetchOptions,
): Promise<z.output<T>> {
  const { response, body } = await sendJson(path, init);

  if (!response.ok) {
    if (response.status === 401 && shouldAttemptRefresh(path, options)) {
      const refreshed = await tryRefreshSession();
      if (refreshed) {
        return apiFetch(path, schema, init, { skipRefresh: true });
      }
    }
    const parsed = apiErrorSchema.safeParse(body);
    const tooLarge = response.status === 413;
    throw new ApiRequestError(
      parsed.success
        ? parsed.data
        : {
            statusCode: response.status,
            code: tooLarge ? 'FILE_TOO_LARGE' : 'UNKNOWN',
            message: tooLarge
              ? 'Die Datei ist zu groß.'
              : 'Der Server hat mit einem unerwarteten Fehler geantwortet.',
            timestamp: new Date().toISOString(),
            path,
          },
    );
  }

  return schema.parse(body);
}

function shouldAttemptRefresh(path: string, options?: FetchOptions): boolean {
  if (options?.skipRefresh) {
    return false;
  }
  const pathname = path.split('?')[0] ?? path;
  return !AUTH_NO_REFRESH.has(pathname);
}

function tryRefreshSession(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      if (locks?.request) {
        return await locks.request('gb-auth-refresh', refreshSession);
      }
      return await refreshSession();
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function refreshSession(): Promise<boolean> {
  try {
    await apiFetch(
      '/auth/refresh',
      loginOkResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      { skipRefresh: true },
    );
    return true;
  } catch {
    return false;
  }
}

async function sendJson(
  path: string,
  init?: RequestInit,
): Promise<{ response: Response; body: unknown }> {
  const headers = new Headers(init?.headers);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  if (init?.body != null && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });

  const body: unknown = await response.json().catch(() => undefined);
  return { response, body };
}
